/**
 * useFilterSet Hook
 *
 * State management for active filters
 */

import { useState, useCallback, useMemo } from 'react';
import {
  ActiveFilter,
  FilterSet,
  FilterSuggestion,
  FilterOperator,
  FilterValue,
} from '../types';

/**
 * Generate a unique ID for filter instances
 */
function generateFilterId(): string {
  return `filter-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Hook return type
 */
export interface UseFilterSetReturn {
  /** Current active filters */
  activeFilters: FilterSet;
  /** Add a filter from a suggestion */
  addFilter: (suggestion: FilterSuggestion) => string;
  /** Add a filter manually */
  addFilterManual: (
    filterId: string,
    operator: FilterOperator,
    value: FilterValue
  ) => string;
  /** Update an existing filter */
  updateFilter: (id: string, operator: FilterOperator, value: FilterValue) => void;
  /** Remove a filter by instance ID */
  removeFilter: (id: string) => void;
  /** Clear all filters */
  clearFilters: () => void;
  /** Check if a specific filter type is active */
  hasFilter: (filterId: string) => boolean;
  /** Get filters of a specific type */
  getFiltersOfType: (filterId: string) => ActiveFilter[];
  /** Replace the entire filter set */
  setFilters: (filters: FilterSet) => void;
}

/**
 * Hook for managing filter state
 *
 * @param initialFilters - Initial filter set (optional)
 * @returns Filter state and actions
 */
export function useFilterSet(initialFilters: FilterSet = []): UseFilterSetReturn {
  const [activeFilters, setActiveFilters] = useState<FilterSet>(initialFilters);

  /**
   * Add a filter from a suggestion
   */
  const addFilter = useCallback((suggestion: FilterSuggestion): string => {
    const newFilter: ActiveFilter = {
      id: generateFilterId(),
      filterId: suggestion.filterId,
      operator: suggestion.prefilledOperator,
      value: suggestion.prefilledValue,
    };

    setActiveFilters((prev) => [...prev, newFilter]);
    return newFilter.id;
  }, []);

  /**
   * Add a filter manually
   */
  const addFilterManual = useCallback(
    (filterId: string, operator: FilterOperator, value: FilterValue): string => {
      const newFilter: ActiveFilter = {
        id: generateFilterId(),
        filterId,
        operator,
        value,
      };

      setActiveFilters((prev) => [...prev, newFilter]);
      return newFilter.id;
    },
    []
  );

  /**
   * Update an existing filter
   */
  const updateFilter = useCallback(
    (id: string, operator: FilterOperator, value: FilterValue): void => {
      setActiveFilters((prev) =>
        prev.map((filter) =>
          filter.id === id ? { ...filter, operator, value } : filter
        )
      );
    },
    []
  );

  /**
   * Remove a filter by instance ID
   */
  const removeFilter = useCallback((id: string): void => {
    setActiveFilters((prev) => prev.filter((filter) => filter.id !== id));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback((): void => {
    setActiveFilters([]);
  }, []);

  /**
   * Check if a specific filter type is active
   */
  const hasFilter = useCallback(
    (filterId: string): boolean => {
      return activeFilters.some((filter) => filter.filterId === filterId);
    },
    [activeFilters]
  );

  /**
   * Get filters of a specific type
   */
  const getFiltersOfType = useCallback(
    (filterId: string): ActiveFilter[] => {
      return activeFilters.filter((filter) => filter.filterId === filterId);
    },
    [activeFilters]
  );

  /**
   * Replace the entire filter set
   */
  const setFilters = useCallback((filters: FilterSet): void => {
    setActiveFilters(filters);
  }, []);

  return useMemo(
    () => ({
      activeFilters,
      addFilter,
      addFilterManual,
      updateFilter,
      removeFilter,
      clearFilters,
      hasFilter,
      getFiltersOfType,
      setFilters,
    }),
    [
      activeFilters,
      addFilter,
      addFilterManual,
      updateFilter,
      removeFilter,
      clearFilters,
      hasFilter,
      getFiltersOfType,
      setFilters,
    ]
  );
}
